// Single per-question "what is this question doing right now" record.
// Folds in the old QuestionStreaming (status / log / tasksha) and the
// per-operation lifecycle markers so callers only have to subscribe to
// one store.
//
// A class (not interface) so derived `is…` checks live alongside the
// fields. TaskManagerService.patchQuestionProcessing rebuilds the
// instance with Object.assign so the prototype is preserved across
// patches; never spread one of these into a plain object literal.
//
// ── Per-op lifecycle pairs ──────────────────────────────────────────────
// The previous shape used a single global `queued` / `running` pair,
// which contaminated cross-operation reads - e.g. firing trace while an
// answer batch was queued made `isqueuedtrace` true even though trace
// hadn't been queued at all. Each operation (answer / trace / scoring)
// now has its own pair so the lifecycles are independent.
//
// Answer pipeline writers (startGenerateQuestions + applyStreamingFromEvent):
//   started → queuedAnswer=true; first non-end event → runningAnswer=true;
//   end → both false. The substep booleans (sourcedocs/structure/answer)
//   continue to track which step inside the answer pipeline is in flight.
//
// Trace / scoring writers (runTrace / runScore - direct API calls, no
// server queue): on entry runningX=true, on exit runningX=false. If a
// future batch ever queues trace/scoring server-side, set queuedX too.
export class QuestionProcessingState {
    // Per-step "currently being generated" flags inside the answer
    // pipeline. Flipped to true at dispatch by startGenerateQuestions,
    // cleared one-by-one as the matching SSE events arrive
    // (sources/structure/answerdone).
    sourcedocs: boolean = false
    structure: boolean = false
    answer: boolean = false

    // Streaming meta. `status` is a human-readable label for the current
    // step; `log` is the timeline of meaningful events (no chunks);
    // `tasksha` is the backend task id that produced the latest event -
    // used by the tkinit reconciler to flip queued ↔ running when SSE
    // drops events.
    // status?: string
    log?: QuestionProcessingLogEntry[]
    tasksha?: string

    // Per-operation queued/running pairs. Independent across operations.
    queuedAnswer?: boolean
    runningAnswer?: boolean

    queuedTrace?: boolean
    runningTrace?: boolean

    queuedScoring?: boolean
    runningScoring?: boolean

    queuedImprovements?: boolean
    runningImprovements?: boolean

    // UTC ms-since-epoch of the most recent event applied to this entry.
    // Server frames carry their own updatetime (set in SseEvent's ctor via
    // DateTimeOffset.UtcNow.ToUnixTimeMilliseconds, so it's timezone-safe
    // regardless of where the API runs). patchQuestionProcessing /
    // applyProgressEvents drop any incoming patch whose updatetime is
    // older than the current value here, so out-of-order or duplicated
    // server frames can't roll the state backwards.
    updatetime: number = 0

    // ── Derived state ───────────────────────────────────────────────────
    // Generic "is in flight" / "is queued" - used by surfaces that gate
    // on any activity.
    get isqueued(): boolean { return !!this.queuedAnswer || !!this.queuedTrace || !!this.queuedScoring || !!this.queuedImprovements }
    get isrunning(): boolean { return !!this.runningAnswer || !!this.runningTrace || !!this.runningScoring || !!this.runningImprovements }
    get isprocessing(): boolean { return this.isqueued || this.isrunning }
    get isdone(): boolean { return !this.isprocessing }

    // Per-operation gating - trivial reads of the matching pair.
    get isqueuedanswer(): boolean { return !!this.queuedAnswer }
    get isrunninganswer(): boolean { return !!this.runningAnswer }
    get isprocessinganswer(): boolean { return this.isqueuedanswer || this.isrunninganswer }

    get isqueuedtrace(): boolean { return !!this.queuedTrace }
    get isrunningtrace(): boolean { return !!this.runningTrace }
    get isprocessingtrace(): boolean { return this.isqueuedtrace || this.isrunningtrace }

    get isqueuedscoring(): boolean { return !!this.queuedScoring }
    get isrunningscoring(): boolean { return !!this.runningScoring }
    get isprocessingscoring(): boolean { return this.isqueuedscoring || this.isrunningscoring }

    get isqueuedimprovements(): boolean { return !!this.queuedImprovements }
    get isrunningimprovements(): boolean { return !!this.runningImprovements }
    get isprocessingimprovements(): boolean { return this.isqueuedimprovements || this.isrunningimprovements }
}

export interface QuestionProcessingLogEntry {
    ts: number;
    step: string;
    text: string;
}
